import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Card, EmptyState, Field, FormError, SubmitButton, StatusBadge } from "@/components/common";
import { fmtDateTime, fmtNumber } from "@/lib/format";
import { PlanGate } from "@/components/PlanGate";
import { Thread, ConvRow, usePoll } from "@/modules/messaging/Thread";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

const FILTERS = ["all", "unread"];

function NewConversation({ open, onClose, preset, onCreated }) {
  const vehicles = useFetch(open ? "/vehicles" : null);
  const [f, setF] = useState({ vehicle_id: preset.vehicle || "", text: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/messages/conversations", f);
      onCreated(data.id);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-conversation-dialog">
        <DialogHeader><DialogTitle>{t("b9.msg.newTitle")}</DialogTitle><DialogDescription>{t("b9.msg.newHint")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          <Field label={t("b9.msg.vehicle")}>
            <Select value={f.vehicle_id} onValueChange={(v) => setF({ ...f, vehicle_id: v })} disabled={!!preset.vehicle}>
              <SelectTrigger data-testid="new-conversation-vehicle"><SelectValue placeholder={t("b9.msg.pickVehicle")} /></SelectTrigger>
              <SelectContent>{(vehicles.data || []).filter((v) => v.customer_id).map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.make} {v.model}{v.customer_name ? ` — ${v.customer_name}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("msg.message")}><Textarea required value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="new-conversation-text" /></Field>
          <SubmitButton busy={busy} disabled={!f.vehicle_id} data-testid="new-conversation-submit">{t("msg.send")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VehiclePanel({ cid, tz }) {
  const { data } = useFetch(`/messages/conversations/${cid}/summary`);
  if (!data) return null;
  const v = data.vehicle;
  const row = (k, val) => <div className="flex justify-between gap-3 text-xs"><span className="text-slate-500">{k}</span><span className="text-right font-medium text-slate-800">{val || "—"}</span></div>;
  return (
    <aside className="space-y-5 border-t border-slate-200 bg-slate-50/60 p-4 text-sm xl:border-l xl:border-t-0" data-testid="vehicle-summary-panel">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("b9.msg.panel")}</p>
      {v ? (
        <div className="space-y-1.5">
          <p className="font-mono-plate text-lg font-bold text-slate-900" data-testid="panel-vehicle-plate">{v.plate}</p>
          <p className="text-slate-700">{v.make} {v.model} {v.year || ""}</p>
          {row(t("b9.msg.odometer"), v.odometer ? `${fmtNumber(v.odometer)} km` : null)}
          {row(t("b9.msg.vin"), v.vin)}
        </div>
      ) : <p className="text-xs text-slate-500">{t("b9.msg.noVehicle")}</p>}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-slate-900">{t("b9.msg.owner")}</p>
        <Link to={`/pro/customers/${data.customer.id}`} className="block font-medium text-teal-700 hover:underline" data-testid="panel-customer-link">{data.customer.name}</Link>
        {data.customer.phone && <a href={`tel:${data.customer.phone}`} className="block text-xs text-slate-600">{data.customer.phone}</a>}
        {data.customer.email && <p className="truncate text-xs text-slate-600">{data.customer.email}</p>}
      </div>
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-slate-900">{t("b9.msg.nextBooking")}</p>
        {data.next_appointment ? <p className="text-xs text-slate-700" data-testid="panel-next-booking">{fmtDateTime(data.next_appointment.start, tz)} · {data.next_appointment.service} <StatusBadge status={data.next_appointment.status} /></p> : <p className="text-xs text-slate-500">{t("b9.msg.noBooking")}</p>}
      </div>
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-slate-900">{t("b9.msg.jobs")}</p>
        {data.jobs.length === 0 ? <p className="text-xs text-slate-500">{t("b9.msg.noJobs")}</p> : data.jobs.map((j) => (
          <Link key={j.id} to={`/pro/jobs/${j.id}`} className="flex items-center justify-between gap-2 rounded-md bg-white px-2 py-1.5 text-xs shadow-sm hover:bg-teal-50" data-testid={`panel-job-${j.number}`}>
            <span className="font-mono font-semibold">{j.number}</span><StatusBadge status={j.status} />
          </Link>
        ))}
      </div>
      {data.maintenance.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-slate-900">{t("b9.msg.maintenance")}</p>
          {data.maintenance.map((m) => <p key={m.id} className="text-xs text-slate-700">• {m.recommendation}</p>)}
        </div>
      )}
    </aside>
  );
}

function InboxInner() {
  const { garage, readOnly } = useGarage();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [list, setList] = useState(null);
  const active = params.get("c");
  const vehicleParam = params.get("vehicle");
  const load = useCallback(() => api.get("/messages/conversations", { params: { filter } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), [filter]);
  usePoll(load);
  useEffect(() => {
    if (!vehicleParam) return;
    api.get(`/messages/by-vehicle/${vehicleParam}`).then((r) => setParams(r.data.id ? { c: r.data.id } : { new: "1", vehicle: vehicleParam })).catch((e) => toast.error(errMsg(e)));
  }, [vehicleParam, setParams]);
  const open = (id) => setParams({ c: id });
  return (
    <div>
      <PageHeader title={t("msg.title")} subtitle={t("msg.subtitle")} actions={!readOnly && <Button onClick={() => setParams({ new: "1" })} data-testid="new-conversation-button"><Plus className="mr-2 h-4 w-4" /> {t("msg.new")}</Button>} />
      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((k) => <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`messages-filter-${k}`}
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${filter === k ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{t(`msg.filters.${k}`)}</button>)}
      </div>
      <div className="gf-card grid overflow-hidden md:grid-cols-[300px_1fr]" data-testid="messages-inbox">
        <div className={`border-r border-slate-200 ${active ? "hidden md:block" : ""}`} data-testid="conversations-list">
          {list && list.length === 0 && <div className="p-6"><EmptyState icon={MessageSquare} title={t("msg.empty")} text={t("msg.emptyText")} /></div>}
          {(list || []).map((c) => <ConvRow key={c.id} c={c} staff active={c.id === active} onClick={() => open(c.id)} tz={garage.timezone} />)}
        </div>
        <div className={active ? "" : "hidden md:block"}>
          {active ? (
            <div className="grid xl:grid-cols-[1fr_260px]">
              <div>
                <button type="button" className="px-4 pt-3 text-sm text-slate-600 md:hidden" onClick={() => setParams({})} data-testid="thread-back">← {t("msg.title")}</button>
                <Thread key={active} path={`/messages/conversations/${active}`} sendPath={`/messages/conversations/${active}/messages`} tz={garage.timezone} readOnly={readOnly} onChange={load} />
              </div>
              <VehiclePanel key={`p-${active}`} cid={active} tz={garage.timezone} />
            </div>
          ) : <p className="p-10 text-center text-sm text-slate-500">{t("msg.pick")}</p>}
        </div>
      </div>
      {params.get("new") === "1" && <NewConversation open preset={{ vehicle: params.get("vehicle") }} onClose={() => setParams({})} onCreated={(id) => { setParams({ c: id }); load(); }} />}
    </div>
  );
}

export default function Inbox() {
  return <PlanGate feature="messaging"><InboxInner /></PlanGate>;
}

export function LatestMessages() {
  const { garage, hasFeature } = useGarage();
  const { data } = useFetch(hasFeature("messaging") ? "/messages/conversations" : null, { limit: 4 });
  if (!hasFeature("messaging") || !data) return null;
  return (
    <Card testId="dashboard-latest-messages" className="min-w-0">
      <div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-bold text-slate-900">{t("msg.latest")}</h2><Link to="/pro/messages" className="text-sm font-semibold text-teal-700" data-testid="dashboard-open-inbox">{t("msg.openInbox")}</Link></div>
      {data.length === 0 ? <p className="text-sm text-slate-500">{t("msg.empty")}</p> : data.slice(0, 4).map((c) => (
        <Link key={c.id} to={`/pro/messages?c=${c.id}`} className="-mx-4 block"><ConvRow c={c} staff tz={garage.timezone} onClick={() => {}} /></Link>
      ))}
    </Card>
  );
}

export function MessageCustomerButton({ vehicleId }) {
  const { hasFeature } = useGarage();
  if (!hasFeature("messaging") || !vehicleId) return null;
  return <Button asChild size="sm" variant="outline" data-testid="message-customer-button"><Link to={`/pro/messages?vehicle=${vehicleId}`}><MessageSquare className="mr-2 h-4 w-4" /> {t("msg.messageCustomer")}</Link></Button>;
}
